"use client"

import { useEffect, useState, useSyncExternalStore } from "react"
import { useSearchParams, useRouter, usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { CheckCircle2, X } from "lucide-react"

const AUTO_DISMISS_MS = 6000

// Hydration detection without setState-in-effect (the house idiom — see
// useSessionState / StickyHeader): the server snapshot is `false` and the client
// snapshot is `true`, so the first client render matches the server output and
// flips to `true` only after hydration commits.
const noopSubscribe = () => () => {}
const getClientSnapshot = () => true
const getServerSnapshot = () => false

type Variant = "verified" | "already-verified" | "sign-in"

/**
 * Reads `?verify=verified` / `?verify=already-verified` from the URL and shows a
 * brief confirmation banner. The verify-email page sets these params after a
 * link click — the banner is the user-facing signal that verification worked.
 *
 * Self-cleaning: strips the param from the URL on mount so a refresh doesn't
 * re-trigger the banner. Auto-dismisses after AUTO_DISMISS_MS, dismissable
 * manually via the close button.
 */
export function VerifyConfirmation() {
  const t = useTranslations("auth.verifyBanner")
  const params = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const verify = params.get("verify")
  const initialVariant: Variant | null =
    verify === "verified"
      ? "verified"
      : verify === "already-verified"
        ? "already-verified"
        : verify === "sign-in"
          ? "sign-in"
          : null

  // D1: capture the variant once at mount and drive rendering from state, not
  // from the URL. We immediately strip `?verify` from the URL inside the
  // effect, which would otherwise flip `shouldShow` to false on the next
  // render and hide the banner before the auto-dismiss timer ever fires.
  const [variant] = useState<Variant | null>(initialVariant)
  const [open, setOpen] = useState(initialVariant !== null)

  // Hydration guard: under PPR (cacheComponents) the prerendered shell is built
  // with empty search params, so the server renders nothing here. Reading the
  // real `?verify` param on the client's first render would add a banner node
  // the server HTML lacks — React #418 hydration mismatch. Gate the param-driven
  // render on `hydrated` so the first client render matches the server output
  // (null); the banner appears once hydration commits.
  const hydrated = useSyncExternalStore(noopSubscribe, getClientSnapshot, getServerSnapshot)

  useEffect(() => {
    if (!variant) return

    // Strip ?verify from the URL so a page refresh doesn't re-show the banner.
    const next = new URLSearchParams(params.toString())
    next.delete("verify")
    const qs = next.toString()
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })

    const id = setTimeout(() => setOpen(false), AUTO_DISMISS_MS)
    return () => clearTimeout(id)
    // We deliberately depend only on `variant` (set once at mount): re-running
    // when `params` changes would re-trigger the URL strip and the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant])

  if (!hydrated || !open || !variant) return null

  const message =
    variant === "already-verified"
      ? t("alreadyVerified")
      : variant === "sign-in"
        ? t("signIn")
        : t("verified")

  return (
    <div
      role="status"
      data-testid="verify-confirmation-banner"
      className="border-b border-success/30 bg-success/10"
    >
      <div className="mx-auto flex max-w-screen-2xl items-center gap-3 px-6 py-3 text-sm">
        <CheckCircle2 className="size-4 text-success shrink-0" aria-hidden="true" />
        <span className="text-foreground">{message}</span>
        <button
          type="button"
          aria-label={t("dismiss")}
          onClick={() => setOpen(false)}
          className="ml-auto rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
